import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import dayjs from "dayjs";
import Slider from '@mui/material/Slider';
import Tooltip from '@mui/material/Tooltip';
import StatusComment from './StatusComment';
import './ObjectiveCard.css';

const getTypeLabel = (key, t) => {
  const map = {
    'ambitious': t('objective_card.type_ambitious'),
    'committed': t('objective_card.type_committed'),
    'learning':  t('objective_card.type_learning'),
    'Амбітна ціль':    t('objective_card.type_ambitious'),
    "Обов'язкова ціль": t('objective_card.type_committed'),
    'Навчальна ціль':  t('objective_card.type_learning'),
    'Ambitious goal':  t('objective_card.type_ambitious'),
    'Committed goal':  t('objective_card.type_committed'),
    'Learning goal':   t('objective_card.type_learning'),
  };
  return map[key] ?? key;
};

const ObjectiveCard = ({
  objective,
  toggleInitiativeCompletion,
  expandAll,
  isCheckBoxDisabled,
  isKrChampion,
  krChampionIds = [],
  onNumericProgressChange,
  isManager,
  isAdmin,
  onObjectiveCommentCreate,
  onObjectiveCommentUpdate,
  onKrCommentCreate,
  onKrCommentUpdate,
  onObjectiveCommentHistoryLoad,
  onKrCommentHistoryLoad,
  statusCommentsEnabled,
}) => {
  const { t } = useTranslation();
  // KR Champion може взаємодіяти тільки зі своїми KR
  const isKrDisabled = (krId) => isCheckBoxDisabled || (isKrChampion && !krChampionIds.includes(krId));
  const canEditObjectiveComment = isManager || isAdmin;
  const canEditKrComment = (krId) => isManager || isAdmin || (isKrChampion && krChampionIds.includes(krId));
  const [expandedKR, setExpandedKR] = useState({});
  
  const [animatedProgress, setAnimatedProgress] = useState(objective.progress);
  const startTimeRef = useRef(null);
  const startValueRef = useRef(objective.progress);

  const rafRef = useRef(null);

  useEffect(() => {
    const duration = 300;
    startValueRef.current = animatedProgress;
    startTimeRef.current = null;

    const animate = (time) => {
      if (!startTimeRef.current) startTimeRef.current = time;
      const elapsed = time - startTimeRef.current;
      const t = Math.min(elapsed / duration, 1);
      const current =
        startValueRef.current +
        (objective.progress - startValueRef.current) * t;

      setAnimatedProgress(current);

      if (t < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(rafRef.current);
  }, [objective.progress]);
  
  // Коли змінюється expandAll, розгортаємо/згортаємо всі KRs
  useEffect(() => {
      const newExpandedState = {};
      objective.key_results.forEach((kr) => {
          newExpandedState[kr.id] = expandAll;
      });
      setExpandedKR(newExpandedState);
  }, [expandAll]);

  const toggleExpand = (krId) => {
      setExpandedKR((prev) => ({
          ...prev,
          [krId]: !prev[krId],
      }));
  };

  const handleSliderChange = (kr) => (_, newProgress) => {
      onNumericProgressChange(kr.id, newProgress);
  };

  // const calcTarget = (kr) => Math.round((kr.weight / 100) * (kr.target_value ?? 0));

  const calculateStep = (target) => {
    if (target >= 1000000) return 10000;
    if (target >= 100000) return 1000;
    if (target >= 10000) return 100;
    if (target >= 1000) return 10;
    return 1;
  };
  

  return (
    <div className="card">
      <div className="card-top">
        <div className="card-top-left">
        <p className="h2">{objective.title}</p>
        <div className="bubbles">
          {objective.type && <p className="bubble p">{getTypeLabel(objective.type, t)}</p>}
          <div className="bubble number">
            <div className="view-weight">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask0_2_2958" style={{maskType:"alpha"}} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
              <rect width="20" height="20" fill="#D9D9D9"/>
              </mask>
              <g mask="url(#mask0_2_2958)">
              <path d="M16.2907 7.97904L15.5247 7.213L16.0999 6.63779C16.1534 6.58432 16.1801 6.52418 16.1801 6.45738C16.1801 6.39057 16.1534 6.3305 16.0999 6.27717L13.7218 3.91988C13.6684 3.8664 13.6084 3.83967 13.5416 3.83967C13.4748 3.83967 13.4146 3.8664 13.3611 3.91988L12.8068 4.49509L12.0407 3.72904L12.6272 3.12175C12.8815 2.86745 13.1876 2.74029 13.5455 2.74029C13.9034 2.74029 14.2095 2.86745 14.4639 3.12175L16.898 5.55592C17.1523 5.81022 17.2795 6.11286 17.2795 6.46384C17.2795 6.81481 17.1523 7.11745 16.898 7.37175L16.2907 7.97904ZM7.26677 17.003C7.10969 17.1601 6.88344 17.2664 6.58802 17.322C6.2926 17.3775 6.01774 17.2781 5.76344 17.0238L3.19469 14.4549C2.93497 14.1995 2.8051 13.8838 2.8051 13.5078C2.8051 13.1318 2.93497 12.814 3.19469 12.5543L3.74906 12.0207L4.5151 12.7868L3.96865 13.3203C3.91517 13.3738 3.88844 13.4339 3.88844 13.5007C3.88844 13.5675 3.91517 13.6276 3.96865 13.6809L6.31802 16.0511C6.37135 16.1046 6.43142 16.1313 6.49823 16.1313C6.56503 16.1313 6.62517 16.1046 6.67865 16.0511L7.23302 15.5047L7.99906 16.2707L7.26677 17.003ZM15.1866 10.9807L16.2955 9.89259C16.349 9.83911 16.3757 9.77765 16.3757 9.70821C16.3757 9.63877 16.349 9.57731 16.2955 9.52384L10.5168 3.74509C10.4633 3.69161 10.4018 3.66488 10.3324 3.66488C10.263 3.66488 10.2015 3.69161 10.148 3.74509L9.03906 4.83321C8.98573 4.88668 8.95906 4.94675 8.95906 5.01342C8.95906 5.08022 8.98573 5.14036 9.03906 5.19384L14.8259 10.9807C14.8794 11.034 14.9395 11.0607 15.0064 11.0607C15.073 11.0607 15.1331 11.034 15.1866 10.9807ZM9.9126 16.2755L11.0007 15.1953C11.0541 15.142 11.0807 15.0819 11.0807 15.0151C11.0807 14.9483 11.0541 14.8882 11.0007 14.8349L5.20573 9.03988C5.1524 8.98654 5.09233 8.95988 5.02552 8.95988C4.95872 8.95988 4.89865 8.98654 4.84531 9.03988L3.7651 10.128C3.71163 10.1815 3.6849 10.2429 3.6849 10.3124C3.6849 10.3818 3.71163 10.4433 3.7651 10.4968L9.54385 16.2755C9.59733 16.329 9.65878 16.3557 9.72823 16.3557C9.79767 16.3557 9.85913 16.329 9.9126 16.2755ZM9.57594 11.8493L11.8822 9.55592L10.4639 8.15842L8.17052 10.4438L9.57594 11.8493ZM10.6705 17.0495C10.4109 17.3092 10.0968 17.439 9.72823 17.439C9.35962 17.439 9.04552 17.3092 8.78594 17.0495L2.99115 11.2338C2.73142 10.9743 2.60156 10.6636 2.60156 10.302C2.60156 9.94029 2.73142 9.62967 2.99115 9.37008L4.07927 8.27384C4.33469 8.01425 4.65038 7.88446 5.02635 7.88446C5.40247 7.88446 5.72031 8.01425 5.9799 8.27384L7.40448 9.67779L9.6899 7.37175L8.2651 5.95988C8.00552 5.70029 7.87573 5.38481 7.87573 5.01342C7.87573 4.64217 8.00552 4.32675 8.2651 4.06717L9.38219 2.97113C9.63761 2.7114 9.9533 2.58154 10.3293 2.58154C10.7052 2.58154 11.023 2.7114 11.2826 2.97113L17.0903 8.73717C17.35 8.99259 17.4799 9.30828 17.4799 9.68425C17.4799 10.0602 17.35 10.378 17.0903 10.6376L15.9734 11.7338C15.7139 11.9934 15.3984 12.1232 15.0272 12.1232C14.6558 12.1232 14.3403 11.9934 14.0807 11.7338L12.6272 10.309L10.342 12.6153L11.7668 14.0399C12.0264 14.2995 12.1596 14.6163 12.1666 14.9903C12.1735 15.3642 12.0472 15.6809 11.7876 15.9405L10.6705 17.0495Z" fill="var(--inverse-color)"/>
              </g>
            </svg>
          {objective.weight}%
            </div>
          </div>
        </div>
        </div>
        <div className="completion-circle"
          style={{
            background: `conic-gradient(var(--primary-color) ${animatedProgress.toFixed(0)}%, transparent ${animatedProgress.toFixed(0)}%)`,
          }}
        >
          <span className="percentage">{objective.progress.toFixed(0)}<sup>%</sup></span>
          <span className="percentage-increase">+{objective.increase.toFixed(0)}<sup>%</sup></span>
        </div>
        {statusCommentsEnabled && (
          <StatusComment
            className="objective-status-comment"
            comment={objective.latest_status_comment}
            canEdit={canEditObjectiveComment}
            onCreate={(text) => onObjectiveCommentCreate(objective.id, text)}
            onUpdate={(commentId, text) => onObjectiveCommentUpdate(objective.id, commentId, text)}
            onLoadHistory={() => onObjectiveCommentHistoryLoad(objective.id)}
            entityTitle={objective.title}
          />
        )}
      </div>
      {objective.key_results.map((kr) => (
        <div key={kr.id} className="kr-container">
          {kr.type === "numeric" ? (
            <>
              <div className="numeric-progress-bar" id={kr.id}>
                <div className="progress-bar-value" style={{display: "flex", justifyContent: "space-between", width: "100%"}}>
                  <Tooltip title={t("objective_card.change_last_month")} placement="right-start">
                    <div style={{display: "flex", alignItems: "center", gap: "5px", fontSize: "14px"}}>
                      <svg width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.19046 9.26904L0.416504 8.49508L5.814 3.09759L8.98067 6.26425L13.7419 1.53988H10.9998V0.456543H15.5832V5.03988H14.4998V2.31404L8.98067 7.81238L5.814 4.64571L1.19046 9.26904Z" fill="var(--primary-color)"/>
                      </svg>
                      <p>+{kr.increase.toFixed(0)}%</p>
                    </div>
                  </Tooltip>
                  <div>
                  {kr.current_progress === 0 ? kr.initial_value : kr.current_progress} ({kr.progress.toFixed(0)}%)
                  
                  </div>
                </div>
              </div>

              <Slider
                value={
                  kr.initial_value > kr.target_value
                    ? (kr.current_progress === 0 ? kr.target_value : kr.initial_value - kr.current_progress + kr.target_value)
                    : kr.current_progress
                }
                defaultValue={0}
                min={Math.min(kr.initial_value, kr.target_value)}
                max={Math.max(kr.initial_value, kr.target_value)}
                step={calculateStep(kr.target_value)}
                onChange={(e, value) => {
                  const actualValue = kr.initial_value > kr.target_value
                    ? kr.target_value + (kr.initial_value - value)
                    : value;

                  handleSliderChange(kr)(e, actualValue);
                }}
                disabled={isKrDisabled(kr.id)}
                sx={{ height: 7 }}
              />
            </>


          ) : (
            <>
            <div className="progress-bar" id={kr.id}>
              <div className="progress-bar-inner" style={{ width: `${kr.progress}%` }}></div>
              <div className="progress-bar-value" style={{display: "flex", justifyContent: "space-between", width: "100%"}}>
                  <Tooltip title={t("objective_card.change_last_month")} placement="right-start">
                    <div style={{display: "flex", alignItems: "center", gap: "5px", fontSize: "14px"}}>
                      <svg width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.19046 9.26904L0.416504 8.49508L5.814 3.09759L8.98067 6.26425L13.7419 1.53988H10.9998V0.456543H15.5832V5.03988H14.4998V2.31404L8.98067 7.81238L5.814 4.64571L1.19046 9.26904Z" fill="var(--primary-color)"/>
                      </svg>
                      <p>+{kr.increase.toFixed(0)}%</p>
                    </div>
                  </Tooltip>
                  <div>
                  {kr.progress.toFixed(0)}%
                  </div>
                </div>
                </div>
            </>
            )
          }
          <div className="kr-header" onClick={() => toggleExpand(kr.id)}>
            <h3>{kr.title}</h3>
            <div className="tags">
              {kr.deadline && <p className="bubble">{dayjs(kr.deadline).format("DD.MM.YYYY")}</p>}
              {kr.champion_name && <p className="bubble">{kr.champion_name}</p>}
              <div className="bubble kr-weight">  
                  <div className="kr-weight-container">            
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <mask id="mask0_2_2958" styles={{maskType:"alpha"}} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                    <rect width="20" height="20" fill="#D9D9D9"/>
                    </mask>
                    <g mask="url(#mask0_2_2958)">
                    <path d="M16.2907 7.97904L15.5247 7.213L16.0999 6.63779C16.1534 6.58432 16.1801 6.52418 16.1801 6.45738C16.1801 6.39057 16.1534 6.3305 16.0999 6.27717L13.7218 3.91988C13.6684 3.8664 13.6084 3.83967 13.5416 3.83967C13.4748 3.83967 13.4146 3.8664 13.3611 3.91988L12.8068 4.49509L12.0407 3.72904L12.6272 3.12175C12.8815 2.86745 13.1876 2.74029 13.5455 2.74029C13.9034 2.74029 14.2095 2.86745 14.4639 3.12175L16.898 5.55592C17.1523 5.81022 17.2795 6.11286 17.2795 6.46384C17.2795 6.81481 17.1523 7.11745 16.898 7.37175L16.2907 7.97904ZM7.26677 17.003C7.10969 17.1601 6.88344 17.2664 6.58802 17.322C6.2926 17.3775 6.01774 17.2781 5.76344 17.0238L3.19469 14.4549C2.93497 14.1995 2.8051 13.8838 2.8051 13.5078C2.8051 13.1318 2.93497 12.814 3.19469 12.5543L3.74906 12.0207L4.5151 12.7868L3.96865 13.3203C3.91517 13.3738 3.88844 13.4339 3.88844 13.5007C3.88844 13.5675 3.91517 13.6276 3.96865 13.6809L6.31802 16.0511C6.37135 16.1046 6.43142 16.1313 6.49823 16.1313C6.56503 16.1313 6.62517 16.1046 6.67865 16.0511L7.23302 15.5047L7.99906 16.2707L7.26677 17.003ZM15.1866 10.9807L16.2955 9.89259C16.349 9.83911 16.3757 9.77765 16.3757 9.70821C16.3757 9.63877 16.349 9.57731 16.2955 9.52384L10.5168 3.74509C10.4633 3.69161 10.4018 3.66488 10.3324 3.66488C10.263 3.66488 10.2015 3.69161 10.148 3.74509L9.03906 4.83321C8.98573 4.88668 8.95906 4.94675 8.95906 5.01342C8.95906 5.08022 8.98573 5.14036 9.03906 5.19384L14.8259 10.9807C14.8794 11.034 14.9395 11.0607 15.0064 11.0607C15.073 11.0607 15.1331 11.034 15.1866 10.9807ZM9.9126 16.2755L11.0007 15.1953C11.0541 15.142 11.0807 15.0819 11.0807 15.0151C11.0807 14.9483 11.0541 14.8882 11.0007 14.8349L5.20573 9.03988C5.1524 8.98654 5.09233 8.95988 5.02552 8.95988C4.95872 8.95988 4.89865 8.98654 4.84531 9.03988L3.7651 10.128C3.71163 10.1815 3.6849 10.2429 3.6849 10.3124C3.6849 10.3818 3.71163 10.4433 3.7651 10.4968L9.54385 16.2755C9.59733 16.329 9.65878 16.3557 9.72823 16.3557C9.79767 16.3557 9.85913 16.329 9.9126 16.2755ZM9.57594 11.8493L11.8822 9.55592L10.4639 8.15842L8.17052 10.4438L9.57594 11.8493ZM10.6705 17.0495C10.4109 17.3092 10.0968 17.439 9.72823 17.439C9.35962 17.439 9.04552 17.3092 8.78594 17.0495L2.99115 11.2338C2.73142 10.9743 2.60156 10.6636 2.60156 10.302C2.60156 9.94029 2.73142 9.62967 2.99115 9.37008L4.07927 8.27384C4.33469 8.01425 4.65038 7.88446 5.02635 7.88446C5.40247 7.88446 5.72031 8.01425 5.9799 8.27384L7.40448 9.67779L9.6899 7.37175L8.2651 5.95988C8.00552 5.70029 7.87573 5.38481 7.87573 5.01342C7.87573 4.64217 8.00552 4.32675 8.2651 4.06717L9.38219 2.97113C9.63761 2.7114 9.9533 2.58154 10.3293 2.58154C10.7052 2.58154 11.023 2.7114 11.2826 2.97113L17.0903 8.73717C17.35 8.99259 17.4799 9.30828 17.4799 9.68425C17.4799 10.0602 17.35 10.378 17.0903 10.6376L15.9734 11.7338C15.7139 11.9934 15.3984 12.1232 15.0272 12.1232C14.6558 12.1232 14.3403 11.9934 14.0807 11.7338L12.6272 10.309L10.342 12.6153L11.7668 14.0399C12.0264 14.2995 12.1596 14.6163 12.1666 14.9903C12.1735 15.3642 12.0472 15.6809 11.7876 15.9405L10.6705 17.0495Z" fill="var(--inverse-color)"/>
                    </g>
                  </svg>
                  </div>
                  <div>{`${kr.weight}%`}</div>
                </div>
            </div>
            <button className={`expand-button ${expandedKR[kr.id] ? "opened" : ""}`}>
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="6" viewBox="0 0 12 6" fill="none">
              <path d="M1 1L6 5L11 1" stroke="#54565A" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            </button>
            {statusCommentsEnabled && (
              <StatusComment
                comment={kr.latest_status_comment}
                canEdit={canEditKrComment(kr.id)}
                onCreate={(text) => onKrCommentCreate(kr.id, text)}
                onUpdate={(commentId, text) => onKrCommentUpdate(kr.id, commentId, text)}
                onLoadHistory={() => onKrCommentHistoryLoad(kr.id)}
                entityTitle={kr.title}
              />
            )}
          </div>
          <div className={`kr-content ${expandedKR[kr.id] ? "expanded" : "collapsed"}`}>
            <div className="initiatives-container">
              {kr.initiatives.map((init) => (
                  <div key={init.id} className="initiative-item" style={{'--after-content': `"${init.weight}"`}}>
                    <p>{init.title}</p>
                    <input
                      disabled={isKrDisabled(kr.id)}
                      type="checkbox"
                      checked={Boolean(init.is_completed)}
                      onChange={(e) => toggleInitiativeCompletion(objective.id, kr.id, init.id, e.target.checked, kr.type)}
                    />
                  </div>
                ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ObjectiveCard;
